import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '#app/utils/misc'

const buttonVariants = cva(
	'erda-button pointer-events-auto relative inline-flex items-center justify-center gap-2 rounded-[10px] text-center text-sm font-semibold leading-5 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-45',
	{
		variants: {
			variant: {
				default:
					'border border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-input bg-background shadow-sm hover:border-purple-300 hover:bg-accent hover:text-accent-foreground',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline !p-0 !h-auto',
				unstyled: '!p-0 !h-auto',
			},
			size: {
				default: 'min-h-11 px-5 py-2.5',
				compact: 'min-h-7 px-2.5 py-0.5 text-xs rounded-lg',
				sm: 'min-h-9 px-3.5 py-1.5 text-xs',
				lg: 'min-h-12 px-6 py-3',
				icon: 'h-10 w-10 shrink-0 p-0',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button'
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				data-button-variant={variant ?? 'default'}
				data-button-size={size ?? 'default'}
				{...props}
			/>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
